import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isNumber } from 'lodash';

import { InternalPage } from 'theSource/components';
import { QuestionnaireContainerStyled } from './QuestionnaireStyled';
import { Question } from './components';
import {
  allRequiredAnswered,
  findQuestion,
  flattenQuestionData,
  initResponses,
  MULTI_CHOICE_SURVEY_TYPE,
  MULTI_SELECT_SURVEY_TYPE,
  SUB_ONLY_TYPE,
  removeBenefitsThatAreRestricted,
  mapQuestionsToBenefits,
} from './utils';
import {
  QuestionnaireRequestDto,
  UnumQuestionnaireRequestDto,
  UserResponseDto,
} from './types';
import {
  mapRequestData,
  mapUnumSurveyRequestData,
  QuestionnaireForm,
} from './components/QuestionnaireForm';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import { useConfigurationQuery } from 'theSource/queries';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { GET_DEPENDENTS_QUERY_KEY } from 'theSource/features/FamilyMembers/utils';
import {
  api,
  queryClient,
  queryKeyGenerator,
  useApiMutation,
} from 'theSource/utils';
import { ErrorResponseDto, ResponseDto } from 'theSource/types';
import { PUT_SURVEY_RESPONSES_QUERY_KEY } from 'enrollment/queries/useBenefitCategoryQuery/utils';
import {
  BackLink,
  BottomNavigation,
  LoaderOverlay,
  PageContentHeaderDisplay,
} from 'enrollment/components';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgLifeEventId,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useRestrictionsQuery } from 'enrollment/queries/useRestrictionsQuery';
import {
  setDisplayToast,
  setToastBenefitLookupCode,
} from 'enrollment/services';
import { ROUTES } from 'enrollment/app/routes';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { formatDescription } from '../Benefits/BenefitsPreview/GroupedBenefits/utils';
import { BenefitDataState } from '..';
import { PSBannerWrapper } from 'enrollment/components/Shared';
import { selectHasPreExistingBenefitElections } from 'enrollment/services/CoveragesByCategory';
import { EsignatureModal } from '../Plans/EsignatureModal';
import { AdditionalPlanElectionDataDto } from '../Plans/PlanCard/types';
import { openEsignatureModal } from '../Plans/EsignatureModal/tests/utils';
import { getShowEsignModal, handlePostElection } from '../Plans/utils';
import {
  handleSavviPostElection,
  hasSavviBenefits,
  useSavviOverRideChosenBenefits,
} from 'enrollment/utils/bundledBenefitsUtils/utils';
import { selectIsAdmin } from '../../../theSource/features';
import { CoverageDto } from '../../queries/useBenefitPlansQuery/types';
import { useOliveAdvisorEnabled } from 'enrollment/features/OliveAdvisor/components/OliveAdvisorEnabled';
import { GET_UNUM_INDEX_KEY } from 'enrollment/queries/useUnumIndexQuery/utils';
import './Questionnaire.css';
import { BenefitByBenefitLoader } from 'enrollment/components/BenefitByBenefitLoader';
import { ThemedHeader } from 'components/ThemedHeader';
import {
  selectEnrollmentLatest,
  selectProfileSteps,
} from 'enrollment/components/BottomNavigation/services';
import { PsButtonTuner } from 'stencilSetup';
import { ToastOptions, useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';

export const Questionnaire = () => {
  const { t } = useTranslation('pages.questionnaire');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAdmin = useSelector(selectIsAdmin);
  const [esignedBenefits, setEsignedBenefits] = useState<number[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);
  const [esignatureCount, setEsignatureCount] = useState(0);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;
  const profileSteps = useSelector(selectProfileSteps);
  const hasPreExistingBenefit = useSelector(
    selectHasPreExistingBenefitElections
  );
  const enrollmentLatest = useSelector(selectEnrollmentLatest);

  // oliveAdvisor POC
  const oliveAdvisorEnabled = useOliveAdvisorEnabled();

  const location = useLocation() as BenefitDataState;
  const { state } = location;
  const benefitId = state?.benefitId;
  const currentBenefitCategory = state?.currentCategory;
  const hasSavvi = useSavviOverRideChosenBenefits();

  const toast = useToastDialog();
  const configuredToast = {
    showToast: (options: ToastOptions) => toast.showToast({ ...options, closeIcon: false }),
    hideToast: toast.hideToast
  };

  const {
    data: dependentsData,
    isLoading: isLoadingDependents,
    isFetching: isFetchingDependents,
  } = useQuery<DependentsResponseDto>(GET_DEPENDENTS_QUERY_KEY, () =>
    api.get<DependentsResponseDto>('/dependents')
  );

  const {
    data: unfilteredQuestionnaireResponse,
    isLoading: isLoadingQuestionnaire,
    isFetching: isFetchingQuestionnaire,
  } = useBenefitCategoryQuery({
    key: queryKeyGenerator(
      `benefits-${enrollmentContextType}`,
      Number(orgPlanYearContextId)
    ),
    enrollmentContextType: enrollmentContextType,
    orgPlanYearId: orgPlanYearContextId,
    enabled: enrollmentContextType !== '',
    lumosApiHost: String(lumosApiHost),
  });

  let questionnaireResponse = unfilteredQuestionnaireResponse;
  let disabledQuestionaires: number[] = [];

  const filterEsignData = (benefit: BenefitDto) => {
    const planEsignData = benefit.plans?.[0]?.esign_data;
    const esignData = {
      ...benefit.coverage?.esign_data,
      ...benefit.plans?.[0]?.esign_data,
      esign_text: planEsignData?.esign_text,
      custom_text: planEsignData?.custom_text,
      admin_esign_text: planEsignData?.admin_esign_text,
    };
    const showEsignatureModal = getShowEsignModal(
      esignData,
      benefit?.coverage as unknown as CoverageDto,
      isAdmin
    );
    const orgBenefitId = benefit.benefit.org_benefit_id;
    return { showEsignatureModal, orgBenefitId, esignData };
  };

  useEffect(() => {
    let esignIncrement = 0;
    const esignConfiguredBenefits: number[] = [];

    questionnaireResponse?.data.benefits.forEach((benefit) => {
      let existingData = true;
      const { showEsignatureModal, orgBenefitId } = filterEsignData(benefit);
      if (
        showEsignatureModal &&
        !esignConfiguredBenefits.includes(orgBenefitId)
      ) {
        esignConfiguredBenefits.push(orgBenefitId);
        existingData = false;
      }
      if (!existingData) {
        esignIncrement++;
      }
    });

    setEsignatureCount(esignIncrement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireResponse]);

  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const orgLifeEventID: number = useSelector(selectEnrollmentOrgLifeEventId);
  const {
    data: restrictions,
    isLoading: isLoadingRestrictions,
    isLoading: isFetchingRestrictions,
  } = useRestrictionsQuery({
    orgLifeEventID: orgLifeEventID,
    orgPlanYearID: orgPlanYearId,
    enabled: orgLifeEventID > 0 && orgPlanYearId > 0,
  });

  // get the survey data from questionnaire response based on org_benefit_id
  if (benefitId) {
    const surveyData = questionnaireResponse?.data?.benefits?.filter(
      (benefit: BenefitDto) => benefit?.benefit?.org_benefit_id === benefitId
    )[0];

    questionnaireResponse = {
      ...questionnaireResponse,
      data: {
        benefits: surveyData ? [surveyData] : [],
        context_lookup_code:
          questionnaireResponse?.data?.context_lookup_code || '',
        cart_total: questionnaireResponse?.data?.cart_total || '',
        active_enrollment_benefit_status:
          questionnaireResponse?.data.active_enrollment_benefit_status || {},
        defined_benefit_contributions: undefined,
      },
      status: questionnaireResponse?.status || '',
    };
  } else {
    // we need to filter surveys to only those in the survey category
    const surveyData = questionnaireResponse?.data?.benefits.filter(
      (benefit: BenefitDto) =>
        benefit?.benefit?.org_benefit_category === 'survey'
    );

    questionnaireResponse = {
      ...questionnaireResponse,
      data: {
        benefits: surveyData ? surveyData : [],
        context_lookup_code:
          questionnaireResponse?.data?.context_lookup_code || '',
        cart_total: questionnaireResponse?.data?.cart_total || '',
        active_enrollment_benefit_status:
          questionnaireResponse?.data.active_enrollment_benefit_status || {},
        defined_benefit_contributions: undefined,
      },
      status: questionnaireResponse?.status || '',
    };
  }
  const flattenedData = flattenQuestionData(questionnaireResponse);
  const mappedQuestionToBenefitIds = mapQuestionsToBenefits(
    questionnaireResponse
  );

  const [responses, setResponses] = useState(() =>
    initResponses(flattenedData, questionnaireResponse)
  );

  if (
    restrictions &&
    questionnaireResponse &&
    enrollmentContextType === 'life_event'
  ) {
    [questionnaireResponse, disabledQuestionaires] =
      removeBenefitsThatAreRestricted(restrictions.data, questionnaireResponse);
  }

  const isAggregated = !Boolean(benefitId);

  const emptyFunction = () => {
    return;
  };
  const renderEsignatureModals = () => {
    const esignConfiguredBenefits: Number[] = [];
    return (
      <>
        {questionnaireResponse?.data.benefits.map((benefit, index) => {
          let existingData = true;
          const { showEsignatureModal, orgBenefitId, esignData } =
            filterEsignData(benefit);
          const carrierLogoUrl =
            benefit.coverage?.carrier_logo_url ||
            benefit.plans?.[0]?.carrier_logo_url;
          const carrierName =
            benefit.coverage?.carrier_name || benefit.plans?.[0]?.carrier;
          if (
            showEsignatureModal &&
            !esignConfiguredBenefits.includes(orgBenefitId)
          ) {
            esignConfiguredBenefits.push(orgBenefitId);
            existingData = false;
          }
          return (
            <Fragment key={index}>
              {showEsignatureModal && !existingData && (
                <EsignatureModal
                  setEsignElectionData={handleElection}
                  carrierLogoUrl={carrierLogoUrl}
                  carrierName={carrierName}
                  data={esignData}
                  planId={benefit.benefit.org_benefit_id}
                  hideChoosePlanButton={true}
                  setModalRequired={emptyFunction}
                />
              )}
            </Fragment>
          );
        })}
      </>
    );
  };
  const renderContent = () => {
    return (
      <>
        {questionnaireResponse?.data.benefits.map((benefit, index) => {
          return (
            <div key={index} className="survey-container">
              {isAggregated && (
                <PageContentHeaderDisplay
                  benefit={benefit.benefit}
                  lumosApiHost={lumosApiHost}
                  displayPageHeader={false}
                  pageContentLookupCode="ENROLL_COVERAGE"
                  orgPlanYearId={benefit.benefit.org_plan_year_id}
                />
              )}
              {flattenedData.map(
                (question, index) =>
                  mappedQuestionToBenefitIds
                    .get(benefit.benefit.org_benefit_id)
                    ?.includes(question.question_id) && (
                    <Question
                      key={`q_${question.question_id}_${index}`}
                      question={question}
                      responses={responses}
                      questionResponse={questionnaireResponse}
                      flattenedData={flattenedData}
                      dependents={dependentsData?.data.dependents}
                      disabled={disabledQuestionaires.includes(
                        benefit.benefit.org_benefit_id
                      )}
                      onChange={handleChange}
                    />
                  )
              )}
            </div>
          );
        })}
        {renderEsignatureModals()}
      </>
    );
  };

  const { isLoading, mutate: SurveyResponsesRequest } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    QuestionnaireRequestDto
  >({
    queryKey: PUT_SURVEY_RESPONSES_QUERY_KEY,
    method: 'put',
    endpoint: `${lumosApiHost}/v2/self_service/benefits/survey`,
    onError: () => {
      setHasApiError(true);
    },
    onSuccess: (data, requestVariables) => {
      if (requestVariables?.esign_checked || requestVariables?.esign_password) {
        const esignedBenefitData = esignedBenefits;
        esignedBenefitData.push(requestVariables.org_benefit_id);
        setEsignedBenefits(esignedBenefitData);
        setEsignatureCount(esignatureCount - 1);
      }
    },
    getSuccessMessage: () => t('surveySaved'),
    getErrorMessage: (error) => error.response?.data?.details,
    toastRef: configuredToast
  });

  const { isLoading: isLoadingUnumSurvey, mutate: UnumSurveyResponsesRequest } =
    useApiMutation<ResponseDto, ErrorResponseDto, UnumQuestionnaireRequestDto>({
      queryKey: PUT_SURVEY_RESPONSES_QUERY_KEY,
      method: 'put',
      endpoint: `${lumosApiHost}/v2/self_service/plugins/unum/process_survey_response/${benefitId}`,
      onSuccess: () => {
        queryClient.invalidateQueries(
          `${GET_UNUM_INDEX_KEY}-${enrollmentContextType}-${benefitId}`
        );
        setIsSubmitted(true);
      },
      onError: () => {
        setHasApiError(true);
      },
      getErrorMessage: (error) => error.response?.data?.details,
      showSuccessSnackbar: false,
      toastRef: configuredToast
    });

  const filteredRequestParameters = () => {
    return mapRequestData({
      questionnaireResponse: questionnaireResponse,
      enrollmentContextType: enrollmentContextType,
    });
  };

  const handleSubmit = () => {
    const requestParameters = filteredRequestParameters();

    requestParameters.forEach((surveyBenefitResponses: any) => {
      const benefit = questionnaireResponse?.data?.benefits.find(
        (benefit: BenefitDto) =>
          benefit?.benefit?.org_benefit_id ===
          surveyBenefitResponses.org_benefit_id
      );
      const esignData =
        benefit?.coverage?.esign_data || benefit?.plans?.[0]?.esign_data;
      const plugin = benefit?.benefit.benefit_plugin?.code;

      if (
        esignData &&
        getShowEsignModal(
          esignData,
          benefit?.coverage as unknown as CoverageDto,
          isAdmin
        )
      ) {
        if (!esignedBenefits.includes(surveyBenefitResponses.org_benefit_id)) {
          openEsignatureModal(surveyBenefitResponses.org_benefit_id);
        }
      } else if (Boolean(plugin)) {
        switch (plugin) {
          case undefined:
            break;
          case 'plansource_connect_unum':
            UnumSurveyResponsesRequest(
              mapUnumSurveyRequestData(questionnaireResponse, benefitId)
            );
            break;
          default:
            break;
        }
      } else {
        SurveyResponsesRequest(surveyBenefitResponses);
      }
    });
    setIsSubmitted(true);
  };

  const handleElection = (
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    const requestParameters = filteredRequestParameters();

    requestParameters.forEach((surveyBenefitResponses) => {
      if (
        surveyBenefitResponses.org_benefit_id === additionalPlanData?.plan_id
      ) {
        if (additionalPlanData?.esign_checked) {
          surveyBenefitResponses.esign_checked =
            additionalPlanData.esign_checked;
        }
        if (additionalPlanData?.esign_password) {
          surveyBenefitResponses.esign_password =
            additionalPlanData.esign_password;
        }
        SurveyResponsesRequest(surveyBenefitResponses);
      }
    });
  };

  const handleChange = (event: Event | React.SyntheticEvent) => {
    if (!event || !event.target) {
      return;
    }

    // Handle both native events and React synthetic events
    const target = event.target as any;
    if (!('value' in target)) {
      return;
    }

    // Use the custom isValid property from the event
    const isValid = target.isValid ?? true;

    let inputValue = target.value;
    
    // For checkboxes and radio buttons, update inputValue based on checked state
    if (target.type === 'checkbox' || target.type === 'radio') {
      inputValue = target.checked ? target.value : '';
    }

    // Get question ID from either id attribute or data attribute
    const questionId = target.id || target.getAttribute('data-input-id') || '';
    
    if (!questionId) {
      return;
    }

    // Extract question ID from format like "q21068292_r-1" or "ft-q21066244_r-1_dep"
    let questionIdMatch = questionId.match(/q(\d+)_r(-?\d+)/);
    
    // Handle numeric input fields with ft- or inch- prefix
    if (!questionIdMatch && (questionId.startsWith('ft-') || questionId.startsWith('inch-'))) {
      questionIdMatch = questionId.match(/[ft|inch]-q(\d+)_r(-?\d+)_dep/);
      if (questionIdMatch) {
        const baseId = questionId.substring(questionId.indexOf('-') + 1);
        const heightFtInput = document.getElementById(`ft-${baseId}`) as HTMLInputElement;
        const heightInInput = document.getElementById(`inch-${baseId}`) as HTMLInputElement;

        if (!heightFtInput || !heightInInput) {
          return;
        }

        // Get the base question ID from one of the height fields
        const baseQuestionId = heightFtInput.getAttribute('data-base-question-id');
        if (!baseQuestionId) {
          return;
        }

        const feetValue = heightFtInput.value.trim();
        const inchesValue = heightInInput.value.trim();

        // Custom validation - ensure values are present and in valid range
        const feetValueNum = parseFloat(feetValue);
        const inchesValueNum = parseFloat(inchesValue);
        
        // First check if values are present and are valid numbers
        const isFeetNumValid = feetValue !== '' && !isNaN(feetValueNum);
        const isInchesNumValid = inchesValue !== '' && !isNaN(inchesValueNum);
        
        // Then check ranges
        const feetValid = isFeetNumValid && feetValueNum > 0 && feetValueNum <= 8;
        const inchesValid = isInchesNumValid && (inchesValueNum === 0 || (inchesValueNum > 0 && inchesValueNum < 12));

        // Both fields must be valid for the height to be valid
        const heightValid = feetValid && inchesValid;

        // Calculate total height in inches
        const totalInches = (feetValueNum * 12) + inchesValueNum;

        // Update the responses array and check if all required questions are answered
        const newResponses = responses.map(response => {
          if (response.question_id.toString() === baseId) {
            return {
              ...response,
              response: heightValid ? [totalInches.toString()] : [],
              isValid: heightValid
            };
          }
          return response;
        });

        // Update responses and validate continue button state
        setResponses(newResponses);
        validateContinueButton(newResponses);
        return;
      }
      return;
    }

    const questionIdFormatted = Number(questionIdMatch[1]);
    const responseIdFormatted = questionIdMatch[2];    
    const question = findQuestion(questionIdFormatted, flattenedData);
    if (!question) {
      return;
    }

    let userResponses = responses;
    if (responses.length < 1) {
      userResponses = initResponses(flattenedData, questionnaireResponse);
    }

    const newResponses = userResponses.map((response) => {
      if (response.question_id !== question.question_id) {
        return response;
      }
      
      if (inputValue === '') {
        if (question.targeted_at_type !== SUB_ONLY_TYPE) {
          return response;
        } else {
          return {
            question_id: response.question_id,
            response: [],
            isValid
          } as UserResponseDto;
        }
      }

      if (target.checked || question.targeted_at_type !== SUB_ONLY_TYPE) {
        if (question.question_type === MULTI_CHOICE_SURVEY_TYPE) {
          return {
            question_id: response.question_id,
            response: [responseIdFormatted],
            isValid
          } as UserResponseDto;
        } else {
          return {
            question_id: response.question_id,
            response: [...response.response, responseIdFormatted],
            isValid
          } as UserResponseDto;
        }
      } else {
        let responseValue = [inputValue];
        if (question.question_type === MULTI_SELECT_SURVEY_TYPE) {
          responseValue = response.response.filter(
            (response) => response !== responseIdFormatted
          );
        }
        return {
          question_id: response.question_id,
          response: responseValue,
          isValid
        } as UserResponseDto;
      }
    });

    // Update responses and validate continue button state
    setResponses(newResponses);
    validateContinueButton(newResponses);
  };

  // Helper function to check if a question should be visible based on parent's answer
  const isQuestionVisible = (question: any, responses: UserResponseDto[]) => {
    // If no parent question, it's always visible
    if (!question.parent_question_id) {
      return true;
    }

    // Find the parent's response
    const parentResponse = responses.find(r => r.question_id === question.parent_question_id);
    if (!parentResponse) {
      return false;
    }

    // For multiple choice questions, check if the selected answer is in trigger_answers
    if (Array.isArray(question.trigger_answers)) {
      return question.trigger_answers.some((trigger: { toString: () => string; }) => 
        parentResponse.response.includes(trigger.toString())
      );
    }

    // For numeric questions, check if the parent's value matches any trigger value
    return question.trigger_answers.some((trigger: string) => 
      parentResponse.response.some(value => value === trigger)
    );
  };

  // Helper function to validate the continue button state
  const validateContinueButton = (currentResponses: UserResponseDto[]) => {
    const responseValidations = currentResponses.map(response => {
      const question = flattenedData.find(q => q.question_id === response.question_id);
      
      // Skip validation if question doesn't exist or shouldn't be visible
      if (!question || !isQuestionVisible(question, currentResponses)) {
        return true;
      }

      const isRequired = question && !question.is_optional;
      const isValidResponse = question?.question_type === 'height' || response.isValid !== false;

      return !isRequired || (response.isValid && isValidResponse);
    });
    
    // All visible responses must be valid
    const allResponsesValid = responseValidations.every(isValid => isValid);

    // First check if all required visible questions are answered
    const areAllAnswered = allRequiredAnswered(currentResponses, flattenedData);
    
    // Only enable continue if all validations pass
    const shouldEnableContinue = areAllAnswered && allResponsesValid;
    
    setIsContinueDisabled(!shouldEnableContinue);
  };

  // Keep track of whether initial validation has been done
  const [initialValidationDone, setInitialValidationDone] = useState(false);

  useEffect(() => {
    // Skip if we don't have the questionnaire data yet
    if (!questionnaireResponse) {
      return;
    }

    const loading = isLoading || isLoadingQuestionnaire || isFetchingQuestionnaire;
    // Only proceed if we haven't done initial validation and loading is complete
    if (!loading) {
      let responsesToValidate;

      if (responses.length < 1) {
        // Initialize responses if none exist
        responsesToValidate = initResponses(flattenedData, questionnaireResponse);
        setResponses(responsesToValidate);
      } else {
        // When returning to the page with existing responses, ensure isValid is set correctly
        responsesToValidate = responses.map(response => {
          // Set isValid based on response value and question type
          const question = flattenedData.find(q => q.question_id === response.question_id);
          
          // Default to true for text fields with non-empty values
          let isValid = true;

          if (question) {
            if (question.question_type === 'height') {
              // For height fields, validate the value is in range
              const heightValue = parseFloat(response.response[0] || '0');
              isValid = !isNaN(heightValue) && heightValue >= 24 && heightValue <= 108;
            } else if (question.question_type === 'number') {
              // For number fields, validate it's a valid number and within range if specified
              const numValue = parseFloat(response.response[0] || '');
              isValid = !isNaN(numValue);
              if (isValid && question.min_value) {
                isValid = numValue >= parseFloat(String(question.min_value));
              }
              if (isValid && question.max_value) {
                isValid = numValue <= parseFloat(String(question.max_value));
              }
            } else {
              // For other fields, just check if there's a non-empty value
              isValid = response.response.length > 0 && response.response.some(value => value.trim() !== '');
            }
          }

          return {
            ...response,
            isValid
          };
        });

        setResponses(responsesToValidate);
      }

      // Run initial validation
      validateContinueButton(responsesToValidate);
      setInitialValidationDone(true);
    }
  // eslint-disable-next-line
  }, [isLoading,
      isLoadingQuestionnaire,
      isFetchingQuestionnaire,
      questionnaireResponse,
      initialValidationDone]
  );



  const description =
    questionnaireResponse?.data.benefits[0]?.benefit.page_content_summary;

  const defaultIncompleteDesc = t('messages.survey:incompleteSurveyMessage');
  const defaultCompleteSurveyDesc = t('messages.survey:completeSurveyMessage');

  const [updatedDescription, setUpdatedDescription] = useState(description);
  const defaultDescription =
    questionnaireResponse?.data.benefits[0]?.coverage !== null
      ? defaultCompleteSurveyDesc
      : defaultIncompleteDesc;

  useEffect(() => {
    setUpdatedDescription(() => {
      return isNumber(benefitId) && description
        ? formatDescription(description) === ''
          ? 'defaultText'
          : description
        : `${t('subtitle')}`;
    });
  }, [benefitId, description, t]);

  // used to prevent navigation until all api submits are successful
  // or rereoute if there are no surveys on the questionnaire page

  useEffect(() => {
    if (
      !isLoading &&
      !isLoadingUnumSurvey &&
      !hasApiError &&
      isSubmitted &&
      esignatureCount === 0 &&
      unfilteredQuestionnaireResponse
    ) {
      if (state?.currentBenefitCategory === undefined) {
        queryClient.invalidateQueries({
          predicate: (query) => {
            if (typeof query.queryKey === 'string') {
              return query.queryKey.startsWith('category');
            }
            return false;
          },
        });
      } else {
        queryClient.invalidateQueries(
          queryKeyGenerator(
            `${state?.currentBenefitCategory}-${enrollmentContextType}`,
            Number(orgPlanYearContextId)
          )
        );
      }

      const benefit = questionnaireResponse.data.benefits[0]?.benefit;
      const benefitLookupCode = benefit?.benefit_lookup_code || '';
      if (benefitLookupCode) {
        dispatch(setToastBenefitLookupCode(benefitLookupCode));
        dispatch(setDisplayToast(true));
      }
      const handlePostElectionRouter =
        hasSavvi &&
        hasSavviBenefits(
          unfilteredQuestionnaireResponse.data.benefits,
          unfilteredQuestionnaireResponse.data.bundled_org_benefit_ids
        )
          ? handleSavviPostElection
          : handlePostElection;
      const postElectionValues = handlePostElectionRouter(
        String(enrollmentContextType),
        Number(orgPlanYearContextId),
        unfilteredQuestionnaireResponse,
        String(benefit?.org_benefit_category),
        Number(benefit?.org_benefit_id)
      );

      const proceedWithNavigation = () => {
        if (isNumber(benefitId)) {
          navigate(postElectionValues.navPath, {
            state: {
              benefitData: postElectionValues.benefitData,
              pendedBenefitData: postElectionValues.pendedBenefitData,
              contextLookup: enrollmentContextType,
              currentBenefitCategory: currentBenefitCategory,
              benefitId:
                postElectionValues.benefitData?.benefit?.org_benefit_id,
            },
          });
        } else if (
          profileSteps.decisionSupport === 'decisioniq' ||
          (profileSteps.decisionSupport === 'bundles' &&
            enrollmentLatest === 'decision_support')
        ) {
          navigate(
            `/enrollment/${ROUTES.enrollment.decisionSupportRouter.path}`
          );
        } else if (oliveAdvisorEnabled) {
          navigate(`/enrollment/${ROUTES.enrollment.oliveAdvisor.path}`);
        } else {
          navigate(`/enrollment/${ROUTES.enrollment.benefits.path}`);
        }
      };
      proceedWithNavigation();
    } else if (hasApiError && !isLoading) {
      setIsSubmitted(false);
      setHasApiError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isLoadingUnumSurvey,
    hasApiError,
    isSubmitted,
    esignatureCount,
    profileSteps,
    navigate,
    benefitId,
    enrollmentContextType,
    state?.currentBenefitCategory,
    oliveAdvisorEnabled,
    state?.currentCategory,
    unfilteredQuestionnaireResponse,
  ]);

  const isLoadingPage =
    isLoadingRestrictions ||
    isFetchingRestrictions ||
    isLoadingQuestionnaire ||
    isFetchingQuestionnaire ||
    isLoadingDependents ||
    isFetchingDependents;

  if (isLoadingPage) {
    return <BenefitByBenefitLoader />;
  }

  return (
    <InternalPage
      testId="questionnaire-page"
      CustomActionComponent={
        isNumber(benefitId) ? (
          <BackLink className="my-3 mt-md-5 mb-md-2 mt-lg-0 pt-md-3_5 pt-lg-0" />
        ) : undefined
      }
      blockClasses={
        isNumber(benefitId)
          ? 'mt-md-5 pb-4 pt-2 px-4 px-md-0 p-lg-0 mt-lg-5'
          : 'mt-md-3_5 mt-lg-4_5 pt-3 px-4 pb-4 pt-md-5 px-md-0 p-lg-0'
      }
    >
      <ThemedHeader>
        {isNumber(benefitId)
          ? questionnaireResponse?.data.benefits[0]?.benefit?.org_benefit_name
          : t('common:enrollment.profileHeader')}
      </ThemedHeader>
      <p className="body1 pb-4">
        {updatedDescription === 'defaultText'
          ? defaultDescription
          : updatedDescription}
      </p>
      <>
        {!benefitId && hasPreExistingBenefit[0] && (
          <PSBannerWrapper
            id="copied-elections-banner"
            message={t('copiedElectionsBanner')}
            level="info"
            icon
            extraClass="mb-4"
          />
        )}
      </>
      <>
        {disabledQuestionaires.length > 0 && (
          <PSBannerWrapper
            id="readOnly-elections-banner"
            message={t('readOnlyBanner')}
            level="info"
            icon
            extraClass="mb-4"
          />
        )}
      </>
      <>{isLoading && esignatureCount === 0 && <LoaderOverlay />}</>

      <>
        <QuestionnaireContainerStyled>
          <QuestionnaireForm children={renderContent()} />
        </QuestionnaireContainerStyled>
        {benefitId ? (
          <div className="py-4 text-center text-md-left">
            <PsButtonTuner
              buttonId="enrollment-continue-button"
              level="primary"
              label={t('enrollment.benefits:LooksGood')}
              disabled={isContinueDisabled}
              onClick={handleSubmit}
            />
          </div>
        ) : (
          <BottomNavigation
            futurePages={
              profileSteps.decisionSupport || profileSteps.chatSupport
                ? [
                    ...(profileSteps.decisionSupport === 'decisioniq'
                      ? [profileSteps.decisionSupport]
                      : []),
                    ...(profileSteps.chatSupport
                      ? [profileSteps.chatSupport]
                      : []),
                  ]
                : undefined
            }
            completePages={['profile', 'family']}
            onPage="questionnaire"
            translatedPage="pages.enrollmentProfile"
            onClickEvent={handleSubmit}
            disableButton={isContinueDisabled}
          />
        )}
      </>
    </InternalPage>
  );
};
